import {
  BaseSalaryComponent,
  BehaviourSettings,
  DeductionType,
  DeductionsComponentType,
  EarningPayType,
  EarningsComponentType,
  NonPayableBenefitsComponentType,
  SalaryComponent,
  SalaryComponentCategory,
} from 'components/SalaryComponents/types';

import { DeductionType as RunPayrollDeductionType } from 'components/RunPayroll/utils/types';
import { GraphQLResponse } from '../types';
import { PayrollPeopleOrganization } from '../user/types';

export type PayrollCreateOrUpdateSalaryComponentResponse = {
  code: number;
  data?: Partial<SalaryComponent>;
  message?: string;
  status_code?: string;
  success: boolean;
};

export type PayrollCreateOrUpdateSalaryComponentVariables = (
  | Omit<EarningsComponentType, 'id'>
  | Omit<DeductionsComponentType, 'id'>
  | Omit<NonPayableBenefitsComponentType, 'id'>
) & {
  id?: string;
};

export type PayrollUpdateSalaryComponentVariables = BaseSalaryComponent & {
  settings: Partial<BehaviourSettings>;
  payType?: EarningPayType;
};

export type SalaryComponentStatusType =
  | 'COMPONENT_STATUS_NONE'
  | 'COMPONENT_STATUS_ACTIVE'
  | 'COMPONENT_STATUS_INACTIVE';

export type PayTypeFilterType =
  | 'LIST_ADHOC_TYPE'
  | 'LIST_VARIABLE_TYPE'
  | 'LIST_FIXED_TYPE'
  | 'LIST_RECURRING_TYPE'
  | 'LIST_NON_PAYABLE_BENEFITS_TYPE'
  | 'LIST_BONUS_TYPE';

export type SalaryComponentSetStatusType = 'SET_STATUS_ACTIVE' | 'SET_STATUS_INACTIVE';

export type TaxabilityFilterType = {
  taxExemptInOldRegime: boolean;
  taxExemptInNewRegime: boolean;
};

export type PayrollListSalaryComponentsQueryVariables = {
  category: SalaryComponentCategory;
  status: SalaryComponentStatusType;
  payType?: PayTypeFilterType;
  taxability?: TaxabilityFilterType;
  deductionType?: DeductionType;
  searchQuery?: string;
  includeBonusComponents?: boolean;
};

export type GetEmployeeAdditionsDeductionsQueryVariables = {
  userId: string;
  effectiveFrom: string;
  effectiveTo: string;
  payType?: PayTypeFilterType;
  includeFnfComponents?: boolean;
};

export type GetEmployeeAdditionsDeductionsResponse = GraphQLResponse<
  'payrollGetEmployeeAdditionsDeductions',
  GetEmployeeAdditionsDeductionsResponseContent
>;

export type UpdateEmployeeAdditionsDeductionsResponse = GraphQLResponse<
  'payrollUpdateEmployeeAdditionsDeductions',
  GetEmployeeAdditionsDeductionsResponseContent
>;

export type ListAdditionsResponse = GraphQLResponse<
  'payrollListAdditions',
  SalaryComponentAssignmentResponse<EarningsComponentType>
>;

export type ListDeductionsResponse = GraphQLResponse<
  'payrollListDeductions',
  SalaryComponentAssignmentResponse<DeductionsComponentType>
>;

export type UpdateAdditionsResponse = GraphQLResponse<
  'payrollUpdateAdditions',
  SalaryComponentAssignmentResponse<EarningsComponentType>
>;

export type UpdateDeductionsResponse = GraphQLResponse<
  'payrollUpdateDeductions',
  SalaryComponentAssignmentResponse<DeductionsComponentType>
>;

export type SalaryComponentAssignmentResponse<
  T extends EarningsComponentType | DeductionsComponentType,
> = {
  data: AssignmentResponseData<T>[];
  source: string;
};

export type GetEmployeeAdditionsDeductionsResponseContent = {
  additions: AssignmentResponseData<EarningsComponentType>[];
  deductions: AssignmentResponseData<DeductionsComponentType>[];
  lopData: {
    peopleId: string;
    lopDays: number;
    source: LopSource;
  }[];
};

type AdditionDeductionType = 'adhoc_payment' | 'recurring';

export type EarningsAdditionalSdiData = {
  addition_type: AdditionDeductionType;
  adhoc_payment_id: string;
};

export type DeductionsAdditionalSdiData = {
  deduction_type: AdditionDeductionType;
  adhoc_payment_id: string;
};

export type AssignmentResponseData<T extends SalaryComponent> = {
  id: string;
  userId: string;
  type: string;
  amount: number;
  effectiveFrom: string;
  effectiveTo: string;
  componentDetails: T;
  additionalSdiData?: T extends EarningsComponentType
    ? EarningsAdditionalSdiData
    : T extends DeductionsComponentType
    ? DeductionsAdditionalSdiData
    : undefined;
};

export type ComponentAssignmentRequestInput = {
  componentId: string;
  value: string;
  includeInCompensation?: boolean;
  includeInPayroll?: boolean;
  payType?: string;
};

type SetSalaryComponentStatusResponseObject = {
  isActive: boolean;
  source: string;
};

export type SetSalaryComponentStatusResponse = GraphQLResponse<
  'payrollSetSalaryComponentStatus',
  SetSalaryComponentStatusResponseObject
>;

export type PayrollGetSalaryComponentQueryVariables = {
  id: string;
  category: SalaryComponentCategory;
  payType?: EarningPayType;
};

type SalaryComponentsListData<T> = {
  source: string;
  salaryComponents: T[];
};

export type ListSalaryComponentsResponse<T = SalaryComponent> = GraphQLResponse<
  'payrollListSalaryComponents',
  SalaryComponentsListData<T>
>;

type SalaryComponentData<T> = {
  source: string;
  salaryComponent: T;
};

export type GetSalaryComponentResponse<T = SalaryComponent> = GraphQLResponse<
  'payrollGetSalaryComponent',
  SalaryComponentData<T>
>;

export type PayrollUpdateAdditionsRequest = {
  assignments: {
    userId: string;
    additions: {
      componentId: string;
      value: string;
      effectiveFrom: string;
      effectiveTo: string;
    }[];
  };
};

export type PayrollUpdateDeductionsRequest = {
  assignments: {
    userId: string;
    effectiveFrom?: string;
    effectiveTo?: string;
    deductions: ComponentAssignmentRequestInput[];
    idsForDeletion: string[];
  }[];
};

export type PayrollUpdateEmployeesAdditionsDeductionsLopRequest = {
  userId: string;
  additions: ComponentAssignmentRequestInput[];
  effectiveFrom: string;
  effectiveTo: string;
  deductions: ComponentAssignmentRequestInput[];
  lopData: LopData;
};

export type UserDeletionPayloadInput = {
  userId: string;
  primaryIds: string[];
  payrollMonth?: string;
};

export type PayrollDeleteComponentAssignments = {
  deletions: UserDeletionPayloadInput[];
};

export type DeleteComponentAssignmentsResponse = {
  code: number;
  success: boolean;
  message: string;
};

export type PayrollGetFnfDataVariables = {
  userId: string;
  deactivateDate: string;
  payType?: PayTypeFilterType;
};

export type PayrollGetUserByIdArgs = {
  userId: string;
};

export type UpdateLopDataItem = {
  peopleId: number;
  lopDays: number;
};

export type GetLopDataItem = {
  peopleId: string;
  lopDays: number;
  source: LopSource;
  payrollMonth: string;
  id: string;
  organizationId: number;
};

export type LopSource = 'manual' | 'manual-attendance' | 'fnf' | 'auto-attendance';

export const LopSourceToDeductionTypeMap: Record<LopSource, RunPayrollDeductionType> = {
  manual: RunPayrollDeductionType.TYPE_MANUAL,
  'manual-attendance': RunPayrollDeductionType.TYPE_MANUAL_ATTENDANCE_LOP,
  fnf: RunPayrollDeductionType.TYPE_MANUAL,
  'auto-attendance': RunPayrollDeductionType.TYPE_AUTO_ATTENDANCE_LOP,
};

export type LopData = {
  payrollMonth: string;
  data: UpdateLopDataItem[];
  source: LopSource;
};

export type PayrollStagingStatus =
  | 'created'
  | 'finalized'
  | 'executed'
  | 'failed'
  | 'skipped'
  | 'none';

export type UserPayrollStatuses = {
  usersStatus: Array<{
    userId: string;
    status: PayrollStagingStatus;
  }>;
  isOrgFinal: boolean;
};

export type FnfConfig = {
  gratuityApplicable: boolean;
  gratuityAmount?: string;
  leaveEncashmentDays?: string;
  leaveEncashmentAmount?: string;
  leaveTypes?: {
    type: string;
    balance: number;
    total: number;
  }[];
};

export type PayrollGetFnfDataResponse = GraphQLResponse<
  'payrollGetFnfData',
  {
    additions: AssignmentResponseData<EarningsComponentType>[];
    deductions: AssignmentResponseData<DeductionsComponentType>[];
    lopData: GetLopDataItem[];
    userData: PayrollPeopleOrganization;
    userStatuses: UserPayrollStatuses;
    fnfConfig: FnfConfig;
  }
>;

export type PayrollGetFnfDataV2Response = GraphQLResponse<
  'payrollGetFnfDataV2',
  {
    additions: AssignmentResponseData<EarningsComponentType>[];
    deductions: AssignmentResponseData<DeductionsComponentType>[];
    lopData: GetLopDataItem[];
    userData: PayrollPeopleOrganization;
    userStatuses: UserPayrollStatuses;
    fnfConfig: FnfConfig;
  }
>;

export type PayrollUpdateFnfDataVariables = {
  deactivateDate: string;
  additions: ComponentAssignmentRequestInput[];
  deductions: ComponentAssignmentRequestInput[];
  deletedAdditionIds: string[];
  deletedDeductionIds: string[];
  lopData: LopData;
  userData: {
    userId: string;
    personalEmailAddress?: string | null;
    reasonForLeaving?: string | null;
    deactivateDate: string;
  };
  fnfConfig: Omit<FnfConfig, 'gratuityApplicable'>;
};

export type PayrollUpdateFnfDataResponse = GraphQLResponse<
  'payrollUpdateFnfData',
  {
    success: boolean;
    payslip_link: string;
    payroll_month: string;
    runpayroll_link: string;
    message_type: string;
  }
>;
export type PayrollUpdateFnfDataV2Response = GraphQLResponse<
  'payrollUpdateFnfDataV2',
  {
    success: boolean;
    payslip_link: string;
    payroll_month: string;
    runpayroll_link: string;
    message_type: string;
  }
>;

export type PayrollSetSalaryComponentStatusRequest = {
  id: string;
  category: SalaryComponentCategory;
  status: SalaryComponentSetStatusType;
  earningsPayType?: EarningPayType;
};
